/**
 * Definition Artefacts Hook
 * Manages Personas, Use Cases, Journeys, Service Maps, and Storyboards with CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type DefinitionType = 'personas' | 'use-cases' | 'journeys' | 'service-maps' | 'storyboards';

export interface DefinitionPersona {
  id: string;
  name: string;
  role: string | null;
  demographics: string | null;
  primaryGoals: string | null;
  challenges: string | null;
  contexts: string | null;
  tools: string | null;
  motivations: string | null;
  accessibilityNeeds: string | null;
  quotes: string | null;
  imageUrl: string | null;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DefinitionAttachment[];
  createdBy: { id: string; name: string | null; email: string };
  linkedUseCases?: { useCase: { id: string; title: string } }[];
  linkedJourneys?: { journey: { id: string; scenario: string } }[];
}

export interface DefinitionUseCase {
  id: string;
  title: string;
  code: string | null;
  actor: string | null;
  description: string | null;
  preconditions: string | null;
  // mainFlow can be string array (from AI) or object array (manual edit)
  mainFlow: string[] | { step: number; action: string }[] | null;
  // exceptionFlows can be string array (from AI) or object array (manual edit)
  exceptionFlows: string[] | { name: string; trigger: string; steps: string[] }[] | null;
  postconditions: string | null;
  businessRules: string | null;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  itemStatus: 'DRAFT' | 'IN_REVIEW' | 'APPROVED';
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DefinitionAttachment[];
  createdBy: { id: string; name: string | null; email: string };
  linkedPersonas?: { persona: { id: string; name: string } }[];
  linkedJourneys?: { journey: { id: string; scenario: string } }[];
}

export interface DefinitionJourney {
  id: string;
  scenario: string;
  description: string | null;
  stages: { name: string; steps: string[]; touchpoints?: string[]; emotions?: string }[];
  pains: string | null;
  opportunities: string | null;
  itemStatus: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETE';
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DefinitionAttachment[];
  createdBy: { id: string; name: string | null; email: string };
  linkedPersonas?: { persona: { id: string; name: string } }[];
  linkedUseCases?: { useCase: { id: string; title: string } }[];
  // Computed field for display
  personaRef?: string;
}

export interface DefinitionServiceMap {
  id: string;
  title: string;
  description: string | null;
  serviceStages: { name: string; actions: string[] }[];
  frontstage: { actor: string; actions: string[]; touchpoints?: string[] }[] | null;
  backstage: { system: string; processes: string[] }[] | null;
  supportProcesses: unknown | null;
  systems: string | null;
  failurePoints: string | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DefinitionAttachment[];
  createdBy: { id: string; name: string | null; email: string };
}

export interface DefinitionStoryboard {
  id: string;
  scenario: string;
  description: string | null;
  frames: { order: number; title: string; description: string; imageUrl?: string }[];
  narrative: string | null;
  assumptions: string | null;
  itemStatus: 'DRAFT' | 'REVIEW' | 'APPROVED';
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DefinitionAttachment[];
  createdBy: { id: string; name: string | null; email: string };
  // Computed field for display
  creatorRef?: string;
}

export interface DefinitionAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  uploadedAt: string;
}

export interface DefinitionVersion {
  id: string;
  version: number;
  snapshot: unknown;
  changelog: string | null;
  source: 'MANUAL' | 'AI_GENERATED' | 'RESTORED' | 'IMPORTED';
  createdAt: string;
  createdBy: { id: string; name: string | null; email: string };
}

export type DefinitionItem = DefinitionPersona | DefinitionUseCase | DefinitionJourney | DefinitionServiceMap | DefinitionStoryboard;

export interface DefinitionArtefact {
  id: string;
  type: 'PERSONAS' | 'USE_CASES' | 'JOURNEYS' | 'SERVICE_MAPS' | 'STORYBOARDS';
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED';
  items: DefinitionItem[];
  versionsCount: number;
  createdAt: string;
  updatedAt: string;
}

// API Functions
async function fetchDefinition(
  projectId: string,
  type: DefinitionType,
  token: string
): Promise<DefinitionArtefact> {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch definition data');
  }

  const data = await response.json();
  return data.data;
}

async function fetchDefinitionRow(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  token: string
): Promise<DefinitionItem> {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch row');
  }

  const data = await response.json();
  return data.data;
}

async function createDefinitionRow(
  projectId: string,
  type: DefinitionType,
  rowData: Record<string, unknown>,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(rowData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create row');
  }

  return response.json();
}

async function updateDefinitionRow(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  rowData: Record<string, unknown>,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(rowData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update row');
  }

  return response.json();
}

async function deleteDefinitionRow(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete row');
  }

  return response.json();
}

async function copyDefinitionRow(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}/copy`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to copy row');
  }

  return response.json();
}

async function updateItemStatus(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED',
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update status');
  }

  return response.json();
}

async function linkArtefacts(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  targetType: DefinitionType,
  targetId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}/link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ targetType, targetId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to link artefacts');
  }

  return response.json();
}

async function unlinkArtefacts(
  projectId: string,
  type: DefinitionType,
  rowId: string,
  linkId: string,
  linkType: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/rows/${rowId}/link/${linkId}?linkType=${linkType}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to unlink artefacts');
  }

  return response.json();
}

async function fetchVersions(
  projectId: string,
  type: DefinitionType,
  token: string
): Promise<DefinitionVersion[]> {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/versions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch versions');
  }

  const data = await response.json();
  return data.data;
}

async function restoreVersion(
  projectId: string,
  type: DefinitionType,
  versionId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/versions/${versionId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to restore version');
  }

  return response.json();
}

async function generateDefinitionContent(
  projectId: string,
  type: DefinitionType,
  prompt: string,
  attachedFiles: { name: string; type: string; size: number }[],
  model: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/definition/${type}/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      prompt,
      attachedFiles,
      model,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error?.message || 'Failed to generate content');
  }

  const data = await response.json();
  // Return the data object directly (backend returns {success: true, data: {...}})
  return data.data || data;
}

async function searchDefinition(
  projectId: string,
  type: DefinitionType,
  params: { status?: string; priority?: string; search?: string },
  token: string
) {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.set('status', params.status);
  if (params.priority) queryParams.set('priority', params.priority);
  if (params.search) queryParams.set('search', params.search);

  const response = await fetch(`/api/projects/${projectId}/definition/${type}/search?${queryParams}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to search');
  }

  const data = await response.json();
  return data.data;
}

// Hooks
export function useDefinition(projectId: string, type: DefinitionType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['definition', projectId, type],
    queryFn: () => fetchDefinition(projectId, type, token!),
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useDefinitionRow(projectId: string, type: DefinitionType, rowId: string) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['definition-row', projectId, type, rowId],
    queryFn: () => fetchDefinitionRow(projectId, type, rowId, token!),
    enabled: !!projectId && !!token && !!rowId,
    staleTime: 30000,
  });
}

export function useDefinitionVersions(projectId: string, type: DefinitionType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['definition-versions', projectId, type],
    queryFn: () => fetchVersions(projectId, type, token!),
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useSearchDefinition(
  projectId: string, 
  type: DefinitionType, 
  params: { status?: string; priority?: string; search?: string }
) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['definition-search', projectId, type, params],
    queryFn: () => searchDefinition(projectId, type, params, token!),
    enabled: !!projectId && !!token && (!!params.status || !!params.priority || !!params.search),
    staleTime: 30000,
  });
}

export function useCreateDefinitionRow(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (rowData: Record<string, unknown>) =>
      createDefinitionRow(projectId, type, rowData, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

export function useUpdateDefinitionRow(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ rowId, data }: { rowId: string; data: Record<string, unknown> }) =>
      updateDefinitionRow(projectId, type, rowId, data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

export function useDeleteDefinitionRow(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (rowId: string) =>
      deleteDefinitionRow(projectId, type, rowId, token!),
    onMutate: async (rowId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['definition', projectId, type] });

      // Snapshot previous value
      const previousArtefact = queryClient.getQueryData<DefinitionArtefact>(['definition', projectId, type]);

      // Optimistically update - remove item immediately
      if (previousArtefact) {
        queryClient.setQueryData<DefinitionArtefact>(['definition', projectId, type], {
          ...previousArtefact,
          items: previousArtefact.items.filter((item: DefinitionItem) => item.id !== rowId),
        });
      }

      return { previousArtefact };
    },
    onError: (_err, _rowId, context) => {
      // Rollback on error
      if (context?.previousArtefact) {
        queryClient.setQueryData(['definition', projectId, type], context.previousArtefact);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

export function useCopyDefinitionRow(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (rowId: string) =>
      copyDefinitionRow(projectId, type, rowId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

export function useUpdateItemStatus(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ rowId, status }: { rowId: string; status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' }) =>
      updateItemStatus(projectId, type, rowId, status, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
    },
  });
}

export function useLinkArtefacts(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ rowId, targetType, targetId }: { rowId: string; targetType: DefinitionType; targetId: string }) =>
      linkArtefacts(projectId, type, rowId, targetType, targetId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId] });
    },
  });
}

export function useUnlinkArtefacts(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ rowId, linkId, linkType }: { rowId: string; linkId: string; linkType: string }) =>
      unlinkArtefacts(projectId, type, rowId, linkId, linkType, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId] });
    },
  });
}

export function useRestoreDefinitionVersion(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (versionId: string) =>
      restoreVersion(projectId, type, versionId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

export function useGenerateDefinition(projectId: string, type: DefinitionType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (params: {
      prompt: string;
      attachedFiles?: { name: string; type: string; size: number }[];
      model?: string;
    }) =>
      generateDefinitionContent(
        projectId,
        type,
        params.prompt,
        params.attachedFiles || [],
        params.model || 'deepseek-chat',
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['definition', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['definition-versions', projectId, type] });
    },
  });
}

